/**
 * Company comparison page — side-by-side analysis of 2-3 companies.
 *
 * AC-002: Route /companies/compare?ids=id1,id2,id3
 * AC-003: Breadcrumb navigation
 * AC-004: Error for invalid ID count
 * AC-019: Export button
 */

import { useSearchParams, Link } from 'react-router-dom';
import { useCompanyComparison } from '../hooks/useCompanies';
import { ComparisonTable } from '../components/comparison/ComparisonTable';
import { FitScoreCard } from '../components/comparison/FitScoreCard';
import { PositioningMap } from '../components/comparison/PositioningMap';
import { api } from '../services/api';

export function CompanyComparisonPage() {
  const [searchParams] = useSearchParams();
  const idsParam = searchParams.get('ids') || '';
  const companyIds = idsParam
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  const isValidCount = companyIds.length >= 2 && companyIds.length <= 3;

  // Hook must be called unconditionally; use enabled flag to control query
  const { data, isLoading, error } = useCompanyComparison(companyIds, {
    enabled: isValidCount,
  });

  const handleExport = async () => {
    try {
      const blob = await api.exportComparison(companyIds);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comparison-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  // AC-004: Invalid ID count
  if (!isValidCount) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-md bg-amber-50 p-6 text-center">
          <h2 className="text-lg font-semibold text-amber-800">Select 2-3 companies to compare</h2>
          <p className="mt-2 text-sm text-amber-700">
            {companyIds.length < 2
              ? 'Not enough companies selected for comparison.'
              : 'Too many companies selected. Maximum is 3.'}
          </p>
          <Link
            to="/companies"
            className="mt-4 inline-flex items-center rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
          >
            Back to Companies
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <nav className="mb-6 text-sm text-gray-500">
          <Link to="/companies" className="hover:text-gray-700">
            Companies
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900">Compare</span>
        </nav>
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-60 bg-gray-200 rounded" />
          <div className="h-64 bg-gray-200 rounded" />
          <div className="h-80 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <nav className="mb-6 text-sm text-gray-500">
          <Link to="/companies" className="hover:text-gray-700">
            Companies
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900">Compare</span>
        </nav>
        <div className="rounded-md bg-red-50 p-6">
          <h2 className="text-lg font-semibold text-red-800">Error</h2>
          <p className="mt-2 text-sm text-red-700">
            {error instanceof Error ? error.message : 'One or more companies not found.'}
          </p>
          <Link
            to="/companies"
            className="mt-4 inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Back to Companies
          </Link>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* AC-003: Breadcrumb */}
      <nav className="mb-6 text-sm text-gray-500" data-testid="breadcrumb">
        <Link to="/companies" className="hover:text-gray-700">
          Companies
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900">Compare</span>
      </nav>

      {/* Page title */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Company Comparison</h1>
        <p className="mt-1 text-sm text-gray-500">
          Side-by-side analysis of {data.columns.length} companies
        </p>
      </div>

      {/* AC-016: Strategic Fit Scores */}
      {data.fit_scores.length > 0 && (
        <div className="mb-8">
          <FitScoreCard fitScores={data.fit_scores} />
        </div>
      )}

      {/* AC-005: Comparison Table */}
      <div className="mb-8 rounded-lg border border-gray-200 bg-white shadow-sm">
        <ComparisonTable columns={data.columns} availableMetrics={data.available_metrics} />
      </div>

      {/* AC-010: Positioning Map */}
      <div className="mb-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <PositioningMap data={data.positioning} columns={data.columns} />
      </div>

      {/* AC-019: Export Button */}
      <div className="flex justify-end">
        <button
          onClick={handleExport}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          data-testid="export-button"
        >
          Export JSON
        </button>
      </div>
    </div>
  );
}

export default CompanyComparisonPage;
